// src/components/ProjectForm.js
import React, { useState, useEffect, useRef } from 'react';
import { api } from '../utils/api';
import { EditIcon, PlusIcon, AlertTriangleIcon } from './Icon';

const TYPES = ['Apartment', 'Villa', 'Plot', 'Commercial', 'Farmhouse', 'Residential Plot Development', 'Residential', 'Industrial'];

const STATUSES = ['Ongoing', 'Upcoming', 'Completed', 'EOI'];

const DEFAULT_FACINGS = 'North,South,East,West,North-East,North-West,South-East,South-West,No Preference';
const DEFAULT_FLOORS  = 'Ground Floor,1st Floor,2nd Floor,3rd Floor,4th Floor,5th Floor,6th Floor,7th Floor,8th Floor,9th Floor,10th Floor,Above 10th Floor,No Preference';
const DEFAULT_BHK     = '2 BHK,3 BHK,4 BHK';

export default function ProjectForm({ project, onClose, onSaved }) {
  const isEdit = !!project;

  const [form, setForm] = useState({
    id:            project?.id || '',
    code_name:     project?.code_name || '',
    prefix:        project?.prefix || '',
    location:      project?.location || '',
    project_type:  project?.project_type || 'Apartment',
    price_range:   project?.price_range || '',
    price_min:     project?.price_min || '',
    price_max:     project?.price_max || '',
    bhk_options:   project?.bhk_options || '',
    floor_options: project?.floor_options || '',
    unit_areas:    project?.unit_areas || '',
    facings:       project?.facings || DEFAULT_FACINGS,
    total_acres:   project?.total_acres || '',
    total_units:   project?.total_units || '',
    expected_launch_date: project?.expected_launch_date || '',
    project_status: project?.project_status || '',
    is_active:     project ? (project.is_active === '1' || project.is_active === 1 ? 1 : 0) : 1,
    sort_order:    project?.sort_order || 0,
  });

  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  // Salespeople who can be assigned to this project.
  const [salespeople, setSalespeople] = useState([]);
  const [assigned, setAssigned] = useState(
    () => new Set((Array.isArray(project?.salespeople) ? project.salespeople : []).map(Number))
  );

  useEffect(() => {
    api.salespeople().then(res => {
      if (res && Array.isArray(res.salespeople)) setSalespeople(res.salespeople);
    }).catch(() => {});
  }, []);

  const toggleAssigned = (id) => setAssigned(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // Prefix is uppercase letters/digits only, max 6 chars. Used to build EOI numbers.
  const updatePrefix = (val) =>
    update('prefix', val.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6));

  const onTypeChange = (val) => {
    setForm(f => {
      const next = { ...f, project_type: val };
      // Auto-clear irrelevant fields based on type
      if (val === 'Plot' || val === 'Farmhouse') {
        if (!f.bhk_options) next.bhk_options = '';
        if (!f.floor_options) next.floor_options = '';
      } else if (val === 'Villa') {
        if (!f.floor_options) next.floor_options = '';
        if (!f.bhk_options) next.bhk_options = DEFAULT_BHK;
      } else if (val === 'Apartment') {
        if (!f.bhk_options) next.bhk_options = DEFAULT_BHK;
        if (!f.floor_options) next.floor_options = DEFAULT_FLOORS;
      } else if (val === 'Commercial') {
        if (!f.floor_options) next.floor_options = DEFAULT_FLOORS;
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrMsg('');

    if (!form.code_name.trim()) { setErrMsg('Code name is required'); return; }
    const prefix = (form.prefix || '').trim().toUpperCase();
    if (!prefix) { setErrMsg('Prefix is required (used to generate EOI numbers, e.g. TRV → EOI-TRV-0001)'); return; }
    if (!/^[A-Z0-9]{2,6}$/.test(prefix)) { setErrMsg('Prefix must be 2–6 letters/digits (A–Z, 0–9), no spaces'); return; }
    if (!form.location.trim())  { setErrMsg('Location is required'); return; }
    if (!form.price_range.trim()) { setErrMsg('Price range is required'); return; }

    setSaving(true);
    const payload = {
      ...form,
      prefix,
      bhk_options:   form.bhk_options.trim()   || null,
      floor_options: form.floor_options.trim() || null,
      unit_areas:    form.unit_areas.trim()    || null,
      facings:       form.facings.trim()       || null,
      total_acres:   form.total_acres.trim()   || null,
      total_units:   form.total_units.trim()   || null,
      expected_launch_date: form.expected_launch_date.trim() || null,
      project_status: form.project_status || null,
      price_min:     form.price_min === '' ? null : Number(form.price_min),
      price_max:     form.price_max === '' ? null : Number(form.price_max),
      sort_order:    Number(form.sort_order) || 0,
      is_active:     form.is_active ? 1 : 0,
      salespeople:   Array.from(assigned),
    };

    try {
      const res = await api.projectSave(payload);
      if (res?.success) {
        onSaved();
      } else {
        setErrMsg(res?.error || 'Save failed');
      }
    } catch (e) {
      setErrMsg('Network error: ' + e.message);
    }
    setSaving(false);
  };

  // Clicking the backdrop intentionally does NOT close the form, so an
  // accidental outside click never discards the data the user has typed.
  // The form is dismissed only via the × button or Cancel.
  return (
    <div className="modal-overlay">
      <div className="modal modal--wide" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {isEdit ? <EditIcon size={18} /> : <PlusIcon size={18} />}
            <span>{isEdit ? 'Edit Project' : 'Add New Project'}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-row2">
            <Field label="Code Name *">
              <input className="modal-input" value={form.code_name} onChange={e => update('code_name', e.target.value)} required placeholder="e.g. SUNRISE HEIGHTS" />
            </Field>
            <Field
              label="Prefix *"
              hint={form.prefix
                ? `EOI number will be EOI-${form.prefix}-0001`
                : 'Unique, 2–6 letters/digits. Builds the EOI number, e.g. TRV → EOI-TRV-0001'}
            >
              <input
                className="modal-input modal-input--upper"
                value={form.prefix}
                onChange={e => updatePrefix(e.target.value)}
                required
                maxLength={6}
                placeholder="e.g. TRV"
              />
            </Field>
          </div>

          <div className="modal-row2">
            <Field label="Project Type *">
              <select className="modal-input" value={form.project_type} onChange={e => onTypeChange(e.target.value)}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Location *">
              <input className="modal-input" value={form.location} onChange={e => update('location', e.target.value)} required placeholder="e.g. Sector 62, Noida, UP" />
            </Field>
          </div>

          <div className="modal-row2">
            <Field label="Price Range (display) *">
              <input className="modal-input" value={form.price_range} onChange={e => update('price_range', e.target.value)} required placeholder="₹85 Lakh – ₹1.8 Crore" />
            </Field>
            <Field label="Sort Order">
              <input type="number" className="modal-input" value={form.sort_order} onChange={e => update('sort_order', e.target.value)} />
            </Field>
          </div>

          <div className="modal-row2">
            <Field label="Price Min (₹)">
              <input type="number" className="modal-input" value={form.price_min} onChange={e => update('price_min', e.target.value)} placeholder="8500000" />
            </Field>
            <Field label="Price Max (₹)">
              <input type="number" className="modal-input" value={form.price_max} onChange={e => update('price_max', e.target.value)} placeholder="18000000" />
            </Field>
          </div>

          <Field label="BHK Options" hint="Comma separated. Leave empty for Plot/Commercial.">
            <input className="modal-input" value={form.bhk_options} onChange={e => update('bhk_options', e.target.value)} placeholder="2 BHK,3 BHK,4 BHK" />
          </Field>

          <Field label="Floor Options" hint="Comma separated. Leave empty for Plot/Villa/Farmhouse.">
            <textarea className="modal-input modal-textarea" value={form.floor_options} onChange={e => update('floor_options', e.target.value)} placeholder="Ground Floor,1st Floor,2nd Floor,..." />
          </Field>

          <Field label="Unit Areas (sq.ft.)" hint="Comma separated numeric values.">
            <input className="modal-input" value={form.unit_areas} onChange={e => update('unit_areas', e.target.value)} placeholder="950,1200,1450,1800,2200" />
          </Field>

          <Field label="Facings" hint="Comma separated direction options.">
            <textarea className="modal-input modal-textarea modal-textarea--sm" value={form.facings} onChange={e => update('facings', e.target.value)} placeholder="North,South,East,West,..." />
          </Field>

          <div className="modal-row2">
            <Field label="Total Area" hint="Shown on the project card, e.g. 45 Acres">
              <input className="modal-input" value={form.total_acres} onChange={e => update('total_acres', e.target.value)} placeholder="e.g. 45 Acres" />
            </Field>
            <Field label="Total Units" hint="e.g. 50 Plots only">
              <input className="modal-input" value={form.total_units} onChange={e => update('total_units', e.target.value)} placeholder="e.g. 50 Plots only" />
            </Field>
          </div>

          <div className="modal-row2">
            <Field label="Expected Launch Date" hint="Format: DD-MM-YYYY (e.g. 01-06-2026)">
              <input className="modal-input" value={form.expected_launch_date} onChange={e => update('expected_launch_date', e.target.value)} placeholder="01-06-2026" />
            </Field>
            <Field label="Project Status" hint="Drives the Ongoing / Upcoming / Completed statistics.">
              <select className="modal-input" value={form.project_status} onChange={e => update('project_status', e.target.value)}>
                <option value="">— Not set —</option>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Sales Team (Sales Audit access)" hint="Selected salespeople will see this project (and its visitors) in the Sales Audit app.">
            <MultiSelectDropdown
              options={salespeople.map(s => ({ value: Number(s.id), label: `${s.name}${s.username ? ` (${s.username})` : ''}` }))}
              selected={assigned}
              onToggle={toggleAssigned}
              placeholder="Select salespeople…"
              emptyText="No salespeople found. Create users with Sales Audit access first."
            />
          </Field>

          <label className="modal-check">
            <input type="checkbox" checked={!!form.is_active} onChange={e => update('is_active', e.target.checked ? 1 : 0)} />
            <span>Active (visible on EOI form)</span>
          </label>

          {errMsg && (
            <div className="modal-err">
              <AlertTriangleIcon size={15} /> {errMsg}
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="modal-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="modal-save" disabled={saving}>
              {saving ? 'Saving...' : (isEdit ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="modal-field">
      <label className="modal-label">{label}</label>
      {children}
      {hint && <div className="modal-hint">{hint}</div>}
    </div>
  );
}

// A multi-select dropdown: a select-styled trigger that opens a checkbox panel.
// `selected` is a Set of option values; `onToggle(value)` flips one.
function MultiSelectDropdown({ options, selected, onToggle, placeholder, emptyText }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const chosen = options.filter(o => selected.has(o.value));
  const summary = chosen.length
    ? (chosen.length <= 2 ? chosen.map(o => o.label).join(', ') : `${chosen.length} selected`)
    : '';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="modal-input"
        onClick={() => options.length && setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, textAlign: 'left', cursor: options.length ? 'pointer' : 'not-allowed' }}
      >
        <span style={{ color: summary ? '#2c2c2c' : '#999', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {options.length ? (summary || placeholder) : emptyText}
        </span>
        <span style={{ color: '#b8862e', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }}>▾</span>
      </button>

      {open && options.length > 0 && (
        <div
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 20,
            background: '#fff', border: '1px solid #d9cba3', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.14)', maxHeight: 220, overflowY: 'auto', padding: 4,
          }}
        >
          {options.map(o => {
            const on = selected.has(o.value);
            return (
              <label
                key={o.value}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', background: on ? '#fdf6e3' : 'transparent', fontSize: 13.5 }}
              >
                <input type="checkbox" checked={on} onChange={() => onToggle(o.value)} />
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
